import React, { useState } from "react";
import { EyeOutlined, EyeInvisibleOutlined, ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContinueButton from "../../../assets/AntDesign/Buttons/ContinueButton";
import { ASSISTANT_ROUTES } from "../../routePaths";
import useAssistantManagement from "../../../assistantDashboard/hooks/useAssistantManagement";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "../../../authDoctor/pages/LoginDoctor/LoginDoctor.css";
import "./LoginAssistant.css";

const SWAP_STORAGE_KEY = "authHeroOnLeft";

const FormInput = ({
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  name,
  autoComplete,
  t,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const actualType = isPassword && showPassword ? "text" : type;

  return (
    <div className="ld-form-item">
      {label && <label htmlFor={name}>{label}</label>}
      <div className="ld-input-wrapper">
        <input
          id={name}
          name={name}
          type={actualType}
          placeholder={placeholder}
          value={value ?? ""}
          onChange={onChange}
          autoComplete={autoComplete}
        />
        {isPassword && (
          <button
            type="button"
            className="ld-password-toggle"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
          >
            {showPassword ? (
              <EyeInvisibleOutlined className="ld-eye-icon" />
            ) : (
              <EyeOutlined className="ld-eye-icon" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default function LoginAssistant() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const initialHeroOnLeft = (() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? true : stored === "true";
  })();

  const [heroOnLeft] = useState(initialHeroOnLeft);
  const { login } = useAssistantManagement();

  const isValid = !!(form.email && form.password);

  const handleChange = (key, value) => {
    setError("");
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpenAssistantInvitation = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleLogin = async () => {
    if (!isValid || loading) return;

    setLoading(true);

    try {
      await login({ email: form.email, password: form.password }).unwrap();
      setLoading(false);
      navigate(ASSISTANT_ROUTES.AVAILABILITY, { replace: true });
    } catch (err) {
      setLoading(false);
      setError(
        String(
          err?.message ||
            t("assistantPages.auth.loginFailed", "Email ou mot de passe invalide")
        )
      );
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin();
    }
  };

  return (
    <div className="signup-container signup-doctor-auth" onKeyDown={onKeyDown}>
      <button
        type="button"
        className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
        onClick={() => navigate(ROUTES.LANDING)}
        aria-label={t("common.backToHome", "Back to home page")}
      >
        <ArrowLeftOutlined />
        <span>{t("common.backToHome", "Back to home page")}</span>
      </button>

      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <aside
          className="signup-hero"
          style={{
            backgroundImage: `url(${chooseImage})`,
          }}
        >
          <div className="signup-hero-glow" />

          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt={t("assistantPages.auth.decorativeIconAlt")} />
          </div>

          <div className="signup-hero-inner">
            <h3 className="signup-hero-title">{t("assistantPages.auth.heroTitle")}</h3>
            <p className="signup-hero-sub">
              {t("assistantPages.auth.heroSubtitle")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleOpenAssistantInvitation}
            >
              <span>{t("assistantPages.auth.continueAsPatient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>

        <section className="signup-form-wrapper">
          <div className="signup-form">
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("auth.signInPrefix")}{" "}
                <span className="signup-form-title-accent">{t("assistantPages.auth.asAssistant")}</span>
              </h1>
              <p className="signup-form-subtitle">{t("assistantPages.auth.loginSubtitle")}</p>
            </div>

            <div className="signup-form-content">
              <div className="ld-form-grid">
                <div className="fld full">
                  <FormInput
                    name="email"
                    label={t("common.email")}
                    type="email"
                    placeholder={t("assistantPages.auth.emailPlaceholder")}
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    autoComplete="email"
                    t={t}
                  />
                </div>

                <div className="fld full">
                  <FormInput
                    name="password"
                    label={t("common.password")}
                    type="password"
                    placeholder="********"
                    value={form.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    autoComplete="current-password"
                    t={t}
                  />
                </div>

                {!!error && (
                  <div className="fld full">
                    <span className="ld-error-message">{error}</span>
                  </div>
                )}

                <div className="ld-button-container">
                  <ContinueButton
                    disabled={!isValid || loading}
                    onClick={(e) => {
                      if (e?.preventDefault) e.preventDefault();
                      handleLogin();
                    }}
                    type="button"
                  >
                    {loading ? t("common.loading") : t("auth.continue")}
                  </ContinueButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}